// src/pages/productos/NuevoProducto.js
import React, { useState, useEffect, useMemo } from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import { Offcanvas, Alert } from 'react-bootstrap'
import { PanelFooter, PanelHeader } from 'components/Panel'
import { FileUpload, FilePreview } from 'components/FileUpload'
import RichTextField from 'components/RichText/RichTextField'
import api from 'api/api'
import { useForm } from 'hooks/useForm'
import { toast } from 'react-toastify'
import { useFileUpload } from 'hooks/useFileUpload'
import Creatable from 'react-select/creatable'
import useNumberInput from 'hooks/useNumberInput'
import {
	HABILITAR_FICHA_TECNICA,
	MODULO_PROMOCIONES,
	MANEJO_CANTIDADES,
	MAX_IMAGENES,
	HABILITAR_ATRIBUTOS
} from 'config/productos'
// Importar componente de atributos
import ProductoAtributosEditor from 'components/ProductoAtributosEditor'

function NuevoProducto(props) {
	const { mostrar, categorias, guardar, cancelar } = props

	// Estado para manejar si el producto tiene precio o no
	const [tienePrecio, setTienePrecio] = useState(true)
	const [loading, setLoading] = useState(false)

	// Estados para atributos
	const [categoriasSeleccionadas, setCategoriasSeleccionadas] = useState([])
	const [atributosHeredados, setAtributosHeredados] = useState([])
	const [atributosValores, setAtributosValores] = useState({})

	const datosIniciales = {
		nombre: '',
		codigo: '',
		marca: '',
		orden: 0,
		descripcion: '',
		descripcionRaw: '',
		destacado: false,
		activo: true,
		precio: '',
		precioPromocion: '',
		cantidad: MANEJO_CANTIDADES ? 0 : null,
		multimediaIds: [],
		jerarquiasIds: [],
		fichaTecnicaId: null
	}

	// Usar el hook para manejar los archivos
	const imagenesUpload = useFileUpload({
		maxFiles: MAX_IMAGENES,
		maxFileSize: 5000000,
		allowedTypes: ['image/jpeg', 'image/png', 'image/webp', '.jpg', '.jpeg', '.png', '.webp'],
		endpoint: 'a/multimedia',
		fieldName: 'foto',
		uploadOnSelect: false
	})

	// Hooks para manejar los inputs numéricos con formato
	const precioInput = useNumberInput((value) => handleChange(value, 'precio'))
	const precioPromocionInput = useNumberInput((value) => handleChange(value, 'precioPromocion'))
	const cantidadInput = useNumberInput((value) => handleChange(value, 'cantidad'))

	// Función para extraer atributos heredados de las categorías seleccionadas
	const extraerAtributosHeredados = (categoriasIds) => {
		if (!categoriasIds || categoriasIds.length === 0 || !categorias) return []

		// Combinar atributos de todas las categorías sin duplicados
		const atributosMap = new Map()

		categoriasIds.forEach(catId => {
			const categoria = categorias.find(c => c.id === catId)
			if (categoria && categoria.atributos && Array.isArray(categoria.atributos)) {
				categoria.atributos.forEach(atributo => {
					if (!atributosMap.has(atributo.id)) {
						atributosMap.set(atributo.id, atributo)
					}
				})
			}
		})

		// Convertir a array y ordenar
		return Array.from(atributosMap.values()).sort((a, b) => a.orden - b.orden)
	}

	const handleClose = () => {
		cancelar()
	}

	const handleProcesar = async (datos) => {
		// Primero subir las imágenes si existen
		if (imagenesUpload.selectedFiles.length > 0) {
			setLoading(true)
			const resultadosSubida = await imagenesUpload.uploadFiles()

			// Obtener los IDs de las imágenes subidas exitosamente
			const idsImagenes = resultadosSubida
				.filter(r => r.success)
				.map(r => r.id)

			// Añadir los IDs a los datos del producto
			datos.multimediaIds = idsImagenes
			setLoading(false)
		}

		// Preparamos los datos para enviar a la API
		const datosProducto = {
			nombre: datos.nombre,
			codigo: datos.codigo || '',
			marca: datos.marca || '',
			orden: datos.orden,
			descripcion: datos.descripcion || '',
			destacado: datos.destacado,
			activo: datos.activo,
			precio: tienePrecio ? datos.precio : 0,
			precioPromocion: (tienePrecio && datos.precioPromocion) ? datos.precioPromocion : null,
			cantidad: MANEJO_CANTIDADES ? datos.cantidad : null,
			agregarMultimediaIds: datos.multimediaIds,
			jerarquiasIds: datos.jerarquiasIds
		}

		// Si hay ficha técnica, incluirla
		if (HABILITAR_FICHA_TECNICA && datos.fichaTecnicaId) {
			datosProducto.fichaTecnicaId = datos.fichaTecnicaId
		}

		// Si hay atributos configurados, incluirlos
		if (HABILITAR_ATRIBUTOS && Object.keys(atributosValores).length > 0) {
			datosProducto.atributosValores = atributosValores
		}

		setLoading(true)
		try {
			await guardar(datosProducto)
			// No necesitamos hacer reset aquí porque el panel se cerrará
			// y al volver a abrirlo, el useEffect se encargará del reset
		} catch (error) {
			console.error('Error al guardar producto:', error)
		} finally {
			setLoading(false)
		}
	}

	const validateFields = (data) => {
		const newErrors = {}
		let firstInvalidId = null

		// Validación del nombre (obligatorio)
		if (!data.nombre.trim()) {
			newErrors.nombre = 'El nombre es obligatorio'
			firstInvalidId = 'nombre'
		}

		// Validación de precio (obligatorio si tienePrecio está activo)
		if (tienePrecio) {
			if (!data.precio || isNaN(data.precio) || parseFloat(data.precio) <= 0) {
				newErrors.precio = 'Debe ingresar un precio válido mayor a cero'
				if (!firstInvalidId) firstInvalidId = 'precio'
			}

			// Validación de precio promoción si está habilitado y tiene un valor
			if (MODULO_PROMOCIONES && data.precioPromocion) {
				if (isNaN(data.precioPromocion) || parseFloat(data.precioPromocion) <= 0) {
					newErrors.precioPromocion = 'El precio de promoción debe ser mayor a cero'
					if (!firstInvalidId) firstInvalidId = 'precioPromocion'
				} else if (parseFloat(data.precioPromocion) >= parseFloat(data.precio)) {
					newErrors.precioPromocion = 'El precio de promoción debe ser menor al precio regular'
					if (!firstInvalidId) firstInvalidId = 'precioPromocion'
				}
			}
		}

		// Validación de cantidad si está habilitado
		if (MANEJO_CANTIDADES && (isNaN(data.cantidad) || parseInt(data.cantidad) < 0)) {
			newErrors.cantidad = 'La cantidad debe ser un número positivo'
			if (!firstInvalidId) firstInvalidId = 'cantidad'
		}

		return { errors: newErrors, firstInvalidId }
	}

	const { data, handleChange, handleSubmit, errors, updateInitialData } = useForm(
		datosIniciales,
		validateFields,
		handleProcesar
	)

	// Resetear todos los estados cuando el panel se ABRE (mostrar cambia a true)
	// Esto garantiza que al crear un nuevo producto, los datos del anterior no persistan
	// IMPORTANTE: Este useEffect debe estar DESPUÉS de useForm para que updateInitialData esté disponible
	useEffect(() => {
		if (mostrar) {
			// Resetear el hook useForm - usar updateInitialData para forzar actualización de datos
			updateInitialData(datosIniciales)
			
			// Resetear imágenes
			imagenesUpload.reset()
			
			// Resetear estados locales
			setTienePrecio(true)
			setCategoriasSeleccionadas([])
			setAtributosHeredados([])
			setAtributosValores({})
			setLoading(false)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mostrar])

	const handleDescripcionChange = (rawContent) => {
		handleChange(rawContent, 'descripcion')
	}

	// Manejar cambio de categorías
	const handleCategoriasChange = (selected) => {
		setCategoriasSeleccionadas(selected || [])
		const jerarquiasIds = selected ? selected.map(item => item.value) : []
		handleChange(jerarquiasIds, 'jerarquiasIds')

		// Actualizar atributos heredados
		if (HABILITAR_ATRIBUTOS) {
			const heredados = extraerAtributosHeredados(jerarquiasIds)
			setAtributosHeredados(heredados)

			// Limpiar valores de atributos que ya no están disponibles
			if (Object.keys(atributosValores).length > 0) {
				const tiposDisponiblesIds = heredados.map(t => t.id)
				const valoresFiltrados = {}
				Object.keys(atributosValores).forEach(tipoId => {
					if (tiposDisponiblesIds.includes(parseInt(tipoId))) {
						valoresFiltrados[tipoId] = atributosValores[tipoId]
					}
				})
				setAtributosValores(valoresFiltrados)
			}
		}
	}

	const handleFichaTecnica = async (event) => {
		if (!HABILITAR_FICHA_TECNICA) return

		const archivo = event.target.files[0]
		if (!archivo) return

		// Verificar que sea un archivo PDF
		if (archivo.type !== 'application/pdf') {
			toast.error('Solo se permiten archivos PDF como ficha técnica')
			return
		}

		setLoading(true)

		const formData = new FormData()
		formData.append('documento', archivo)

		try {
			const { datos } = await api.post('a/documentos', formData)
			handleChange(datos.id, 'fichaTecnicaId')
			toast.success('Ficha técnica subida correctamente')
		} catch (error) {
			toast.error('Error al subir la ficha técnica')
			console.error(error)
		} finally {
			setLoading(false)
		}
	}

	// Manejar cambios en atributos
	const handleAtributosChange = (nuevosValores) => {
		setAtributosValores(nuevosValores)
	}

	// Calcular si hay atributos configurados
	const tieneAtributosConfigurados = useMemo(() => {
		return Object.keys(atributosValores).length > 0 &&
			Object.values(atributosValores).some(arr => arr.length > 0)
	}, [atributosValores])

	// render
	return (
		<Offcanvas
			show={mostrar}
			onHide={handleClose}
			placement="end"
			scroll="true"
			backdrop="static"
			className="producto-form"
		>
			<PanelHeader
				onClick={handleClose}
				iconClass="fa-arrow-left"
				btnText="Atrás"
				text="Nuevo producto"
			/>

			<Offcanvas.Body id="nuevo-producto">
				<Form onSubmit={handleSubmit}>
					<Row>
						<Col sm={12} md={6}>
							<h5 className="mb-3">Información básica</h5>

							<Form.Group className="mb-3" controlId="nombre">
								<Form.Label>Nombre*</Form.Label>
								<Form.Control
									type="text"
									value={data.nombre}
									onChange={(e) => handleChange(e.target.value, 'nombre')}
									isInvalid={!!errors.nombre}
								/>
								<Form.Control.Feedback type="invalid">{errors.nombre}</Form.Control.Feedback>
							</Form.Group>

							<Form.Group className="mb-3" controlId="codigo">
								<Form.Label>Código</Form.Label>
								<Form.Control
									type="text"
									value={data.codigo}
									onChange={(e) => handleChange(e.target.value, 'codigo')}
									isInvalid={!!errors.codigo}
								/>
								<Form.Control.Feedback type="invalid">{errors.codigo}</Form.Control.Feedback>
							</Form.Group>

							<Form.Group className="mb-3" controlId="marca">
								<Form.Label>Marca</Form.Label>
								<Form.Control
									type="text"
									value={data.marca}
									onChange={(e) => handleChange(e.target.value, 'marca')}
									placeholder="Ej: Samsung, Apple, Nike..."
								/>
								<Form.Text className="text-muted">
									Marca o fabricante del producto (opcional).
								</Form.Text>
							</Form.Group>

							<Form.Group className="mb-3" controlId="orden">
								<Form.Label>Orden</Form.Label>
								<Form.Control
									type="number"
									min="0"
									value={data.orden}
									onChange={(e) => handleChange(e.target.value, 'orden')}
									isInvalid={!!errors.orden}
								/>
								<Form.Control.Feedback type="invalid">{errors.orden}</Form.Control.Feedback>
								<Form.Text className="text-muted">
									Define el orden de aparición en listados. Valores menores aparecen primero.
								</Form.Text>
							</Form.Group>

							<Form.Group className="mb-3" controlId="categorias">
								<Form.Label>Categorías</Form.Label>
								<Creatable
									isMulti
									value={categoriasSeleccionadas}
									options={categorias.map(cat => ({ label: cat.nombre, value: cat.id }))}
									onChange={handleCategoriasChange}
									placeholder="Selecciona o crea categorías"
									formatCreateLabel={(inputValue) => `Crear categoría "${inputValue}"`}
								/>
								<Form.Text className="text-muted">
									Puedes seleccionar categorías existentes o crear nuevas escribiendo el nombre.
									{HABILITAR_ATRIBUTOS && ' Los atributos se heredan de las categorías seleccionadas.'}
								</Form.Text>
							</Form.Group>

							<hr className="my-4" />

							<h5 className="mb-3">Estados y configuración</h5>
							<Row className="mb-3">
								<Col sm={6}>
									<Form.Check
										type="switch"
										id="destacado"
										label="Destacado"
										checked={data.destacado}
										onChange={(e) => handleChange(e.target.checked, 'destacado')}
									/>
									<Form.Text className="text-muted">
										Los productos destacados se muestran en secciones especiales.
									</Form.Text>
								</Col>
								<Col sm={6}>
									<Form.Check
										type="switch"
										id="activo"
										label="Activo"
										checked={data.activo}
										onChange={(e) => handleChange(e.target.checked, 'activo')}
									/>
									<Form.Text className="text-muted">
										Solo los productos activos se muestran en el catálogo.
									</Form.Text>
								</Col>
							</Row>

							<hr className="my-4" />

							<h5 className="mb-3">Gestión de precios</h5>
							<div className="precio-section">
								<Form.Check
									type="switch"
									id="tienePrecio"
									label="Tiene precio"
									checked={tienePrecio}
									onChange={(e) => setTienePrecio(e.target.checked)}
									className="mb-3"
								/>

								{tienePrecio && (
									<div className="campos-relacionados">
										<Row>
											<Col sm={6}>
												<Form.Group className="mb-3" controlId="precio">
													<Form.Label>Precio*</Form.Label>
													<Form.Control
														type="text"
														value={precioInput.formatDisplayValue(data.precio)}
														onChange={precioInput.handleInputChange}
														onBlur={precioInput.handleBlur}
														isInvalid={!!errors.precio}
													/>
													<Form.Control.Feedback type="invalid">{errors.precio}</Form.Control.Feedback>
												</Form.Group>
											</Col>

											{MODULO_PROMOCIONES && (
												<Col sm={6}>
													<Form.Group className="mb-3" controlId="precioPromocion">
														<Form.Label>Precio promoción</Form.Label>
														<Form.Control
															type="text"
															value={precioPromocionInput.formatDisplayValue(data.precioPromocion)}
															onChange={precioPromocionInput.handleInputChange}
															onBlur={precioPromocionInput.handleBlur}
															isInvalid={!!errors.precioPromocion}
														/>
														<Form.Control.Feedback type="invalid">{errors.precioPromocion}</Form.Control.Feedback>
													</Form.Group>
												</Col>
											)}
										</Row>
									</div>
								)}
							</div>

							{MANEJO_CANTIDADES && (
								<>
									<h5 className="mb-3 mt-4">Inventario</h5>
									<Form.Group className="mb-3" controlId="cantidad">
										<Form.Label>Cantidad en stock</Form.Label>
										<Form.Control
											type="text"
											value={cantidadInput.formatDisplayValue(data.cantidad)}
											onChange={cantidadInput.handleInputChange}
											onBlur={cantidadInput.handleBlur}
											isInvalid={!!errors.cantidad}
										/>
										<Form.Control.Feedback type="invalid">{errors.cantidad}</Form.Control.Feedback>
									</Form.Group>
								</>
							)}

							<hr className="my-4" />

							<h5 className="mb-3">Descripción</h5>
							<RichTextField
								value={data.descripcion}
								onChange={handleDescripcionChange}
								label="Información detallada del producto"
							/>

							{/* SECCIÓN DE ATRIBUTOS */}
							{HABILITAR_ATRIBUTOS && (
								<>
									<hr className="my-4" />
									<h5 className="mb-3">
										<i className="fa fa-tags me-2"></i>
										Atributos / Variantes
									</h5>

									{categoriasSeleccionadas.length === 0 ? (
										<Alert variant="secondary">
											<i className="fa fa-info-circle me-2"></i>
											Selecciona al menos una categoría para ver los atributos disponibles.
										</Alert>
									) : atributosHeredados.length === 0 ? (
										<Alert variant="info">
											<i className="fa fa-info-circle me-2"></i>
											Las categorías seleccionadas no tienen atributos configurados.
											<br />
											<small className="text-muted">
												Puedes agregar atributos a las categorías desde la sección de Categorías.
											</small>
										</Alert>
									) : (
										<>
											<p className="text-muted small mb-3">
												Selecciona qué valores de atributos están disponibles para este producto.
											</p>

											<ProductoAtributosEditor
												atributosHeredados={atributosHeredados}
												valoresSeleccionados={atributosValores}
												onChange={handleAtributosChange}
												disabled={loading}
												loading={false}
												modo="editar"
												compacto={true}
											/>

											{tieneAtributosConfigurados && (
												<Alert variant="success" className="mt-3">
													<i className="fa fa-check-circle me-2"></i>
													{Object.values(atributosValores).flat().length} valor(es) de atributos configurado(s).
												</Alert>
											)}
										</>
									)}
								</>
							)}
						</Col>

						<Col sm={12} md={6}>
							<h5 className="mb-3">Imágenes del producto</h5>
							<Alert variant="info">
								Puedes subir hasta {MAX_IMAGENES} imágenes. La primera imagen será la principal.
							</Alert>

							<FileUpload
								title="Imágenes del producto"
								dragDropLabel="Arrastrar a esta área o"
								buttonText="Subir"
								buttonTextPlural="imágenes"
								buttonTextSingular="imagen"
								buttonIconClass="fas fa-file-upload"
								updateFiles={imagenesUpload.handleSelectFiles}
								maxFileSizeInBytes={5000000}
								accept="image/jpeg, image/png, image/webp"
								multiple
								showPreview={false}
							/>

							<div className="mt-4">
								{imagenesUpload.selectedFiles.length > 0 && (
									<FilePreview
										files={imagenesUpload.selectedFiles}
										onRemove={(file) => imagenesUpload.markFileForDeletion(file)}
										showFileSize={true}
										title="Imágenes seleccionadas"
										showPrincipalBadge={true}
										useImageCover={false}
									/>
								)}
							</div>

							{HABILITAR_FICHA_TECNICA && (
								<>
									<hr className="my-4" />

									<h5 className="mb-3">Ficha técnica (PDF)</h5>
									<Form.Group controlId="fichaTecnica" className="mb-3">
										<Form.Label>Seleccionar archivo PDF</Form.Label>
										<Form.Control
											type="file"
											onChange={handleFichaTecnica}
											accept=".pdf"
											disabled={loading || imagenesUpload.isLoading}
										/>
										<Form.Text className="text-muted">
											Tamaño máximo: 5MB
										</Form.Text>
									</Form.Group>

									{data.fichaTecnicaId && (
										<Alert variant="success" className="mt-2">
											<i className="fa fa-file-pdf me-2"></i>
											Ficha técnica subida correctamente
										</Alert>
									)}
								</>
							)}
						</Col>
					</Row>
				</Form>
			</Offcanvas.Body>

			<PanelFooter>
				<Button
					variant="outline-secondary"
					onClick={handleClose}
					className="me-2"
					disabled={loading || imagenesUpload.isLoading}
				>
					Cancelar
				</Button>
				<Button
					variant="primary"
					onClick={handleSubmit}
					disabled={loading || imagenesUpload.isLoading}
				>
					{loading || imagenesUpload.isLoading ?
						<><i className="fa fa-spinner fa-spin me-2"></i> Guardando...</> :
						'Guardar producto'}
				</Button>
			</PanelFooter>
		</Offcanvas>
	)
}

export default NuevoProducto